import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { formatUSDT, shortAddress } from '../../utils/format';

const UserManager = ({ prefillTarget }) => {
  const { contract } = useWeb3();
  const sectionRef = useRef(null);

  const [lookup, setLookup] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const runLookup = async (rawValue) => {
    if (!contract) return;
    const value = (rawValue ?? lookup).toString().trim();
    if (!value) return;

    setBusy('lookup');
    try {
      let userId;
      if (ethers.utils.isAddress(value)) {
        userId = await contract.addressToId(value);
      } else if (value.match(/^[0-9]+$/)) {
        userId = ethers.BigNumber.from(value);
      } else {
        notify.error('Enter a valid wallet address or numeric ID');
        setBusy('');
        return;
      }
      const id = Number(userId.toString());
      if (id === 0) {
        notify.warning('User not found');
        setResult(null);
        setBusy('');
        return;
      }
      const [basic, ext, blacklisted] = await Promise.all([
        contract.getUserBasicInfo(id),
        contract.getUserExtendedInfo(id),
        contract.blacklistedUsers(id)
      ]);
      setResult({
        id,
        wallet: basic.wallet,
        totalInvestment: basic.totalInvestment.toString(),
        activeInvestment: basic.activeInvestment.toString(),
        totalEarned: basic.totalEarned.toString(),
        pendingRewards: basic.pendingRewards.toString(),
        directReferrals: ext.directReferrals.toString(),
        teamVolume: ext.teamVolume.toString(),
        referrerId: ext.referrerId.toString(),
        active: ext.active,
        blacklisted
      });
    } catch (err) {
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  // React to external selection (from UserList).
  // prefillTarget is { value, tick } — tick changes on every click so the
  // same row can be re-selected to re-trigger the lookup.
  useEffect(() => {
    if (prefillTarget && prefillTarget.value) {
      setLookup(prefillTarget.value);
      runLookup(prefillTarget.value);
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // eslint-disable-next-line
  }, [prefillTarget && prefillTarget.tick]);

  const setBlacklist = async (status) => {
    if (!contract || !result) return;
    setBusy('bl');
    let lid = null;
    try {
      lid = notify.loading(`${status ? 'Blacklisting' : 'Unblacklisting'}...`);
      const tx = await contract.blacklistUser(result.id, status);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success(`User ${status ? 'blacklisted' : 'unblacklisted'}`);
      setResult({ ...result, blacklisted: status });
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <div ref={sectionRef}>
      <Card title="User Lookup & Blacklist" subtitle="Search by wallet address or numeric user ID">
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Wallet address or User ID"
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="0x... or numeric ID"
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <Button
              variant="primary"
              loading={busy === 'lookup'}
              onClick={() => runLookup()}
              disabled={!lookup}
            >Lookup</Button>
          </div>
        </div>

        {result && (
          <div style={{
            marginTop: 12,
            padding: 18,
            background: 'var(--bg-input)',
            border: '1px solid var(--border-secondary)',
            borderRadius: 'var(--radius-md)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{
                  fontFamily: 'Orbitron, sans-serif',
                  fontSize: 16,
                  fontWeight: 700
                }}>User #{result.id}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono' }}>
                  {shortAddress(result.wallet)}
                </div>
                {result.referrerId !== '0' && (
                  <div style={{ fontSize: 11, color: 'var(--accent-purple)', marginTop: 4 }}>
                    Upline: #{result.referrerId}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {result.blacklisted && <span className="badge badge-pink">Blacklisted</span>}
                {result.active && !result.blacklisted && <span className="badge badge-green">Active</span>}
                {!result.active && !result.blacklisted && <span className="badge badge-muted">Inactive</span>}
              </div>
            </div>
            <div className="grid grid-3" style={{ gap: 12 }}>
              <div>
                <div className="stat-label">Total Invested</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
                  {formatUSDT(result.totalInvestment)} USDT
                </div>
              </div>
              <div>
                <div className="stat-label">Active</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
                  {formatUSDT(result.activeInvestment)} USDT
                </div>
              </div>
              <div>
                <div className="stat-label">Total Earned</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
                  {formatUSDT(result.totalEarned)} USDT
                </div>
              </div>
              <div>
                <div className="stat-label">Pending</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
                  {formatUSDT(result.pendingRewards)} USDT
                </div>
              </div>
              <div>
                <div className="stat-label">Directs</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>{result.directReferrals}</div>
              </div>
              <div>
                <div className="stat-label">Team Volume</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
                  {formatUSDT(result.teamVolume)} USDT
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              {!result.blacklisted ? (
                <Button
                  variant="danger"
                  size="sm"
                  loading={busy === 'bl'}
                  onClick={() => setBlacklist(true)}
                >Blacklist User</Button>
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  loading={busy === 'bl'}
                  onClick={() => setBlacklist(false)}
                >Remove Blacklist</Button>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserManager;
